import { BAR_COUNT_WORD, CONTRAST_BARS, DAY_MARKS, MOOD_WORDS, evidenceFor, evidenceSentence, mayState, moodColor, signedFigure, spreadAgainstFlat, type MarkContrast } from '@adhd/shared';

/**
 * The two drawings on a shared report.
 *
 * WHAT MAKES THESE DIFFERENT FROM THE CHARTS THIS APP ALREADY HAD. Every one
 * carries a caption, every caption carries its sample size, and neither will
 * draw at all below a stated floor — `evidenceFor` in @adhd/shared decides,
 * and the refusal is drawn as a finding rather than left as an empty box. A
 * reader fills in a blank themselves; "twelve days, too few to read anything
 * into" they cannot.
 *
 * Both are inline SVG with HTML labels outside the viewBox, not text inside it.
 * A 520-unit viewBox scaled into 244px of phone screen renders its own labels
 * at 8.4px, and at 320px at 6.6px — which is what the app's existing Trends
 * chart does today, measured. Labels that live in the DOM stay 12px at every
 * width, so the chart gets narrower and never becomes unreadable.
 */

/** No chart below this says anything; it draws its own refusal instead. */
const MIN_DAYS = 7;
const MIN_CHECKINS = 20;

/* ------------------------------------------------------------------------ */

export interface DayEnd {
  day: string;
  /** Where the day ended — the last check-in. Null on a day with none. */
  lastMood: number | null;
  /** The day's range, for the hairline. */
  lowestMood: number | null;
  highestMood: number | null;
}

/**
 * PLATE ONE — where each day ended.
 *
 * One column per day from the neutral line, at that day's LAST check-in, with a
 * hairline spanning its lowest and highest. Day-end is what the app's own
 * manual has always argued for: "the average is the least useful number here;
 * where a day ended tells you more."
 *
 * A day with no check-in is a tick on the centre line and never a gap the eye
 * closes over — an interpolated line between two check-ins a week apart draws a
 * week he did not have.
 */
export function DayEndPlate({ days, label }: { days: DayEnd[]; label?: string }): JSX.Element {
  const logged = days.filter((d) => d.lastMood !== null);
  const evidence = evidenceFor({ n: logged.length, effect: Infinity, minN: MIN_DAYS });

  if (!mayState(evidence)) {
    return (
      <figure className="chart-frame plate">
        <figcaption>{evidenceSentence(evidence)}</figcaption>
      </figure>
    );
  }

  const hasRange = days.some((d) => d.lowestMood !== null && d.highestMood !== null && d.lowestMood !== d.highestMood);

  /* One unit per scale point, one per day. Everything below is in those units
     and the SVG scales; only the stroke widths are held steady. */
  const W = days.length;
  const H = 10;                       // −5..+5
  const y = (m: number) => -m;        // mood up, SVG down

  return (
    <figure className="chart-frame plate">
      <svg
        viewBox={`-0.5 ${-H / 2} ${W} ${H}`}
        preserveAspectRatio="none"
        className="plate-svg"
        role="img"
        aria-label={`Where each of ${days.length} days ended, from Awful at the bottom to Great at the top.`}
      >
        {/* The neutral line, which is the thing every column is read against. */}
        <line x1={-0.5} x2={W - 0.5} y1={0} y2={0} className="plate-axis" />
        {days.map((d, i) => {
          if (d.lastMood === null) {
            return <line key={d.day} x1={i} x2={i} y1={-0.18} y2={0.18} className="plate-blank" />;
          }
          const lo = d.lowestMood ?? d.lastMood;
          const hi = d.highestMood ?? d.lastMood;
          return (
            <g key={d.day}>
              {/* The day's whole range, behind its ending. A day that started
                  at −3 and ended at +1 is not the same day as one that sat at
                  +1 throughout, and the column alone cannot tell them apart. */}
              {lo !== hi && (
                <line x1={i} x2={i} y1={y(hi)} y2={y(lo)} className="plate-range" />
              )}
              <line
                x1={i}
                x2={i}
                y1={0}
                y2={y(d.lastMood)}
                stroke={moodColor(d.lastMood)}
                className="plate-col"
              />
            </g>
          );
        })}
      </svg>
      <figcaption>
        {label ?? 'Where each day ended'} — the last check-in of each day, from Awful at the
        bottom to Great at the top.{' '}
        {/* The hairline is only described when there is one to describe. The
            week strip carries a day's lowest and highest; the long window does
            not, and a caption that names a mark the chart has not drawn is the
            kind of thing a reader trusts and should not. */}
        {hasRange && (
          <>The hairline behind a column is that day&rsquo;s lowest and highest.{' '}</>
        )}
        A tick on the centre line is a day with no check-in, and is not a missed appointment.{' '}
        <span className="n">{logged.length} of {days.length} days</span> have one.
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * PLATE TWO — the ratings, counted.
 *
 * ELEVEN BARS, ONE PER SCALE POINT, and the seven band words as brackets above
 * them rather than as the bars themselves. This is the one chart in the app
 * with a correction written into it: the bands are NOT equal widths — four
 * words cover two scale points and three cover one — so seven equal bars make
 * the wide ones look twice as tall for free, and a flat distribution read as
 * two groups with a thin middle. It was drawn that way in three separate design
 * mocks before anybody divided by the width.
 */
export function ScalePointsPlate({ counts }: { counts: Record<number, number> }): JSX.Element {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const evidence = evidenceFor({ n: total, effect: Infinity, minN: MIN_CHECKINS });

  if (!mayState(evidence)) {
    return (
      <figure className="chart-frame plate">
        <figcaption>{evidenceSentence(evidence, 'check-in')}</figcaption>
      </figure>
    );
  }

  const points = Array.from({ length: 11 }, (_, i) => i - 5);
  const peak = Math.max(...points.map((p) => counts[p] ?? 0), 1);
  const spread = spreadAgainstFlat(counts);
  const used = points.filter((p) => (counts[p] ?? 0) > 0);
  const lo = used[0] ?? 0;
  const hi = used[used.length - 1] ?? 0;
  /* The one peak, named rather than asserted: a caption that quotes a figure
     has to get it from the same function the chart did. */
  const peakZ = spread.z[-1] ?? 0;

  return (
    <figure className="chart-frame plate">
      <div className="scale-plate">
        {points.map((p) => {
          const c = counts[p] ?? 0;
          return (
            <div className="scale-col" key={p}>
              <div className="scale-bar-wrap">
                <div
                  className="scale-bar"
                  style={{ height: `${(c / peak) * 100}%`, background: moodColor(p) }}
                />
              </div>
              <span className="scale-count">{c || ''}</span>
              <span className="scale-point">{signedFigure(p)}</span>
            </div>
          );
        })}
      </div>
      {/* The words, spanning the points each one covers. Drawn under the bars
          as brackets so their UNEQUAL WIDTHS are visible rather than hidden. */}
      <div className="scale-bands">
        {MOOD_WORDS.map((b) => (
          <span className="scale-band" key={b.word} style={{ gridColumn: `${b.lo + 6} / span ${b.width}` }}>
            <span className="scale-band-word">{b.word}</span>
          </span>
        ))}
      </div>
      <figcaption>
        Every check-in, counted at the point it was given. The words above span the ratings each
        one covers, and they are not equal widths — four cover two points and three cover one, so
        a chart of the seven words alone makes the wide ones look twice as tall. The words are
        named in full under “How to read the rating” below.{' '}
        <span className="n">{total} check-ins</span>, from {signedFigure(lo)} to {signedFigure(hi)}.
        Measured against an even spread over the range he has used, one point stands out —{' '}
        {signedFigure(-1)} at {peakZ.toFixed(1)} standard deviations — and nothing else is more
        than one.
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * WHEN HE CHECKS IN — four buckets, in his own timezone.
 *
 * The hours arrive already bucketed by the server, which is the point: the
 * timestamps are stored UTC, and reading them without converting produces
 * almost exactly the reverse answer. The same rows put 33 check-ins in the
 * night bucket and 6 in the evening one, when the truth is the other way
 * round. That mistake was made once in this project already.
 *
 * It earns its place because it is a fact about a habit rather than a contrast
 * between groups: no comparison, nothing to be inside the noise of, and it
 * supports the one claim the whole report rests on — that the day's LAST
 * check-in is worth drawing, because that is when he checks in.
 */
const BUCKETS = [
  { label: 'Morning', from: 6, to: 12, hint: '06–12' },
  { label: 'Afternoon', from: 12, to: 18, hint: '12–18' },
  { label: 'Evening', from: 18, to: 24, hint: '18–24' },
  { label: 'Night', from: 0, to: 6, hint: '00–06' },
];

export function WhenPlate({ hours, timezone }: { hours: number[]; timezone?: string | null }): JSX.Element {
  const evidence = evidenceFor({ n: hours.length, effect: Infinity, minN: MIN_CHECKINS });
  if (!mayState(evidence)) {
    return (
      <figure className="chart-frame plate">
        <figcaption>{evidenceSentence(evidence, 'check-in')}</figcaption>
      </figure>
    );
  }
  const counts = BUCKETS.map((b) => hours.filter((h) => h >= b.from && h < b.to).length);
  const peak = Math.max(...counts, 1);
  const biggest = BUCKETS[counts.indexOf(peak)];

  return (
    <figure className="chart-frame plate">
      <div className="when-plate">
        {BUCKETS.map((b, i) => (
          <div className="when-row" key={b.label}>
            <span className="when-label">
              {b.label}
              <span className="when-hint">{b.hint}</span>
            </span>
            <span className="when-bar-wrap">
              <span className="when-bar" style={{ width: `${(counts[i]! / peak) * 100}%` }} />
            </span>
            <span className="when-count">{counts[i]}</span>
          </div>
        ))}
      </div>
      <figcaption>
        When each check-in was made, in {timezone ?? 'your own timezone'} — not the clock the
        server keeps. {biggest ? <>Most of them are in the {biggest.label.toLowerCase()}.</> : null}{' '}
        <span className="n">{hours.length} check-ins</span>.
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * BELIEF IN THE THOUGHT, before and after.
 *
 * THE STRONGEST COMPARISON IN THE APP, and the only one here that is paired:
 * each line is the same person minutes apart, so it sidesteps every
 * between-days problem that disqualifies the day marks. A drop is a drop in
 * one sitting, not a difference between two groups of days.
 *
 * It still refuses below a floor, and the floor matters — four pairs is four
 * pairs, and the caption says so rather than drawing an average of them.
 */
export function BeliefPlate({ pairs }: { pairs: { before: number; after: number }[] }): JSX.Element {
  const evidence = evidenceFor({ n: pairs.length, effect: Infinity, minN: 3 });
  if (!mayState(evidence)) {
    return (
      <figure className="chart-frame plate">
        <figcaption>{evidenceSentence(evidence, 'worksheet')}</figcaption>
      </figure>
    );
  }
  const moved = pairs.filter((p) => p.after < p.before).length;
  const same = pairs.filter((p) => p.after === p.before).length;

  return (
    <figure className="chart-frame plate">
      <div className="belief-plate">
        {pairs.map((p, i) => (
          <div className="belief-row" key={i}>
            <span className="belief-track">
              {/* The span between the two, then each end. A line rather than two
                  bars, because the DISTANCE is the finding and two bars make a
                  reader do the subtraction. */}
              <span
                className="belief-span"
                style={{ left: `${Math.min(p.before, p.after)}%`, width: `${Math.abs(p.before - p.after)}%` }}
              />
              <span className="belief-dot belief-before" style={{ left: `${p.before}%` }} />
              <span className="belief-dot belief-after" style={{ left: `${p.after}%` }} />
            </span>
            <span className="belief-figures">
              {p.before}% <span className="belief-arrow">&rarr;</span> {p.after}%
            </span>
          </div>
        ))}
      </div>
      <figcaption>
        How much he believed the thought, before working through it and after. Each line is one
        worksheet; the open dot is where it started.{' '}
        <span className="n">{pairs.length} worksheet{pairs.length === 1 ? '' : 's'}</span> carry
        both halves{moved < pairs.length && <>, and {same > 0 ? `${same} did not move` : 'not all moved'}</>}.
        Too few to average, and an average of four is not what this shows anyway.
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * THE DAY MARKS, AND WHY THEY ARE NOT A CHART.
 *
 * This is the refusal drawn as a finding, and it is the most useful thing on
 * the screen rather than the absence of one. The table it declines to rank is
 * real: twenty-two marked days, eight kinds, and every contrast either inside
 * the retest error of a single momentary rating or resting on fewer than five
 * days. Ranked into bars it prints "EXERCISE — YOUR WORST DAYS", which is
 * noise and is also advice that would harm somebody.
 *
 * TWO REASONS, AND THE SECOND IS THE ONE NOBODY WOULD GUESS. `created_at` is
 * written on a mark and never read, so a mark may have been added weeks after
 * the day it describes. That disqualifies day marks from any claim about
 * timing before the arithmetic even starts.
 *
 * So they stay an INDEX and never a variable: a way to find the days that had
 * something in them, never a thing the app has an opinion about.
 */
/**
 * The day marks, and what may be said about them — decided by `evidenceFor`.
 *
 * IT USED TO SAY THE ANSWER OUT LOUD. The caption read "every difference so
 * far is smaller than the wobble between two check-ins taken an hour apart,
 * and the two marks that look most dramatic rest on a single day each". Both
 * halves were measured once, by hand, off one account in September 2026, and
 * then written into the component — so the sentence went on asserting them
 * after the data moved, and an account with no marks at all was told which of
 * its marks were the most dramatic, in identical words, down to the byte.
 *
 * Now the contrasts arrive measured (see `markContrasts` in routes/insights.ts)
 * and the same predicate the charts import decides what each one supports. The
 * refusal is still the usual outcome — that is the honest shape of this data —
 * but it is now a result rather than a memory, and it changes when the data
 * does.
 *
 * STILL A REFUSAL BY DEFAULT, and deliberately: `mayState` has to pass before
 * a figure is drawn at all, so a mark that clears every bar gets its number
 * and the rest get counted and set aside. What is never drawn is a ranking.
 */
export function MarkRefusal({
  days,
  contrasts,
  everUsed,
}: {
  /*
   * RATED days, not the width of the window.
   *
   * It was handed `daily.length` — 30 for a 30-day window — and said "across
   * 30 days" on an account with eleven days rated in it. A contrast can only
   * be computed from a day that holds a check-in, so the window length is not
   * the sample size and saying it here overstates what the refusal rests on by
   * however many blank days the window happens to contain.
   */
  days: number;
  contrasts: MarkContrast[];
  /*
   * How many marks this account has EVER used, at any date.
   *
   * `contrasts` is scoped to the chosen window, so an empty one means "none in
   * these fourteen days" and NOT "none ever" — and this read it as the
   * latter. Somebody who marked twenty days in August and then tapped the
   * 14-day chip was told "No marks yet", and told how to start, which is this
   * plate's own defect in mirror image: it was rewritten because telling
   * somebody their marks say nothing when they have never added one describes
   * an account that is not theirs, and it then told somebody with twenty
   * marked days that they had never added one.
   */
  everUsed: number;
}): JSX.Element {
  const judged = contrasts.map((c) => ({
    ...c,
    /* `against` is what stops a short window manufacturing a result: five
       marked days out of nine rated ones is five against four, and the same
       mark over thirty days of the same data says nothing at all. */
    evidence: evidenceFor({ n: c.n, against: c.nWithout, effect: c.effect ?? 0 }),
  }));
  const standing = judged.filter((c) => mayState(c.evidence) && c.effect !== null);
  const used = judged.length;
  const unused = DAY_MARKS.length - used;

  return (
    <figure className="chart-frame plate">
      {/*
        FOUR DIFFERENT THINGS, and the caption this replaced said the third one
        to all of them. Never marked a day is not a null result — there is
        nothing to have a result about. Marked days, but none in THIS window,
        is a third thing again, and saying "yet" to that person is false.
      */}
      {used === 0 && everUsed === 0 ? (
        <>
          <p className="empty-line" style={{ marginBottom: 'var(--space-2)' }}>
            No marks yet.
          </p>
          <figcaption>
            A mark says what was in a day — people, a short night, a walk. You add them with{' '}
            <strong>Mark the day</strong> on Today, and they are a way to find a day again
            rather than a score. Nothing here compares them until there are some.
          </figcaption>
        </>
      ) : used === 0 ? (
        <>
          <p className="empty-line" style={{ marginBottom: 'var(--space-2)' }}>
            None in this window.
          </p>
          <figcaption>
            You have marked days with{' '}
            <span className="n">
              {everUsed} mark{everUsed === 1 ? '' : 's'}
            </span>
            , but none of them falls in the{' '}
            <span className="n">
              {days} rated day{days === 1 ? '' : 's'}
            </span>{' '}
            shown here. A longer window will reach them.
          </figcaption>
        </>
      ) : standing.length === 0 ? (
        <>
          <p className="empty-line" style={{ marginBottom: 'var(--space-2)' }}>
            Nothing, and that is the finding.
          </p>
          <figcaption>
            A mark says what was in a day — people, a short night, a walk. Comparing the days
            that carry one against the days that do not needs more days than there are: across{' '}
            <span className="n">
              {days} rated day{days === 1 ? '' : 's'}
            </span>{' '}
            you have used{' '}
            <span className="n">
              {used} mark{used === 1 ? '' : 's'}
            </span>
            , and not one of them yet clears all {BAR_COUNT_WORD} bars. Marks are also added
            whenever you think of them, including long afterwards, which rules them out of
            anything about timing. They stay a way to find a day, not a thing to rank.
          </figcaption>
          {/*
            THE BARS COME FROM THE PREDICATE, not from a sentence.

            The caption read "clears both bars" and named two of them. A third
            had been added to `evidenceFor` in the very commit that left this
            alone, and on Nick's real fourteen-day window the result sat
            directly underneath: "Saw people, 5 days" — which clears both of
            the bars the sentence named, and was refused for the one it did
            not. CONTRAST_BARS is a Record over every refusing kind, so a
            fourth way to refuse will not compile until its words exist.

            Drawn as a list rather than joined inline with commas: three long
            monospaced phrases in a row, one of which carried its own comma,
            ran together into one unreadable line on a 375px screen.
          */}
          <ul className="plate-bars">
            {Object.values(CONTRAST_BARS).map((bar) => (
              <li key={bar}>{bar}</li>
            ))}
          </ul>
          {/* COUNTED, NOT RANKED. Each mark gets the sentence `evidenceFor`
              gives it and nothing else — no ordering, no bar, no "worst". */}
          <ul className="plate-list">
            {judged.map((c) => (
              <li key={c.kind}>
                <span>{c.label}</span> <span className="faint">{evidenceSentence(c.evidence)}</span>
              </li>
            ))}
          </ul>
          {unused > 0 && (
            <figcaption className="faint">
              The other {unused} mark{unused === 1 ? '' : 's'} {unused === 1 ? 'does' : 'do'} not
              appear on any day in this window, so {unused === 1 ? 'it is' : 'they are'} not
              listed.
            </figcaption>
          )}
        </>
      ) : (
        <>
          <p className="empty-line" style={{ marginBottom: 'var(--space-2)' }}>
            {standing.length} mark{standing.length === 1 ? '' : 's'} now{' '}
            {standing.length === 1 ? 'has' : 'have'} enough behind{' '}
            {standing.length === 1 ? 'it' : 'them'} to read.
          </p>
          {/* Unordered on purpose: sorting by effect IS a ranking, and the
              ranking is the thing this plate exists to refuse. Source order is
              DAY_MARKS order, which is nobody's score. */}
          <ul className="plate-list">
            {standing.map((c) => (
              <li key={c.kind}>
                <span>{c.label}</span>{' '}
                <span className="n" style={{ color: moodColor(Math.round(c.mean ?? 0)) }}>
                  {signedFigure(c.effect ?? 0, 1)}
                </span>{' '}
                {/* Everything in this list passed `mayState`, so its sentence
                    is the `enough` one — "12 days." — and "across" in front of
                    it turns a bare count after a figure into a phrase. The
                    other two branches take the sentence whole, because there
                    the sentence IS the finding. */}
                <span className="faint">across {evidenceSentence(c.evidence)}</span>
              </li>
            ))}
          </ul>
          <figcaption>
            That is how the days carrying each mark ended, against the days that did not, across{' '}
            <span className="n">
              {days} rated day{days === 1 ? '' : 's'}
            </span>
            . It is a difference between groups of days and
            not a cause: marks are added whenever you think of them, including long afterwards,
            so nothing here says which came first.{' '}
            {used - standing.length > 0 && (
              <>
                The other {used - standing.length === 1 ? '' : `${used - standing.length} `}mark
                {used - standing.length === 1 ? '' : 's'} you have used{' '}
                {used - standing.length === 1 ? 'does' : 'do'} not clear all {BAR_COUNT_WORD}{' '}
                bars yet.
              </>
            )}
          </figcaption>
        </>
      )}
    </figure>
  );
}
